<template>
  <div class="counters-wrapper">
    <AudienceCounter :setted-count="props.settedAudienceCount" />
    <SegmentCostCounter
      :setted-cost="props.settedSegmentCost"
      ref="costCounterRef"
    />
    <div
      v-if="!isSegmentSetted"
      v-tooltip.bottom="{
        value: countDisabledReason || '',
        class: 'white-tooltip-with-arrow',
        disabled: isReadyToCount || !countDisabledReason,
      }"
    >
      <Button
        label="Обновить"
        severity="secondary"
        icon="pi pi-refresh"
        class="refresh-button"
        @click="updateBothCounters"
        :disabled="!isReadyToCount"
        :loading="isLoading"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import Button from "primevue/button";
import AudienceCounter from "./AudienceCounter.vue";
import SegmentCostCounter from "./SegmentCostCounter.vue";
import { useCategoriesStore } from "@/store/catalog_data/categories";
import { useSegmentsStore } from "@/store/segments/segments";
import { getFilterWithCategories } from "@/services/utils/Objects";
import { rootGetters } from "@/main";

const categoryStore = useCategoriesStore();
const segmentStore = useSegmentsStore();

const props = defineProps<{
  filter: any;
  isReadyToCount: boolean;
  countDisabledReason?: string | null;
  settedAudienceCount: number | null;
  settedSegmentCost: number | null;
  isSegmentSetted?: boolean;
}>();

const emits = defineEmits(["updateAudienceCount", "updateSegmentCost"]);

const isLoading = ref(false);
const costCounterRef = ref<InstanceType<typeof SegmentCostCounter> | null>(
  null
);

const updateBothCounters = async () => {
  isLoading.value = true;

  try {
    const filter = getFilterWithCategories(
      props.filter,
      categoryStore.categoriesMap
    );

    // Параллельно запрашиваем аудиторию и стоимость
    const [count, costKopecks] = await Promise.all([
      segmentStore.getAudienceFilter(filter),
      segmentStore.getAudienceCost(filter),
    ]);

    // Конвертируем копейки в рубли для отображения
    const costRubles = costKopecks / 100;

    emits("updateAudienceCount", count);
    emits("updateSegmentCost", costRubles);

    if (costCounterRef.value) {
      costCounterRef.value.updateCost(costRubles);
    }
  } catch (error: any) {
    rootGetters.toast.show(
      "error",
      "Ошибка",
      error.message ||
        error.response?.data?.detail ||
        "Произошла ошибка при расчете стоимости сегмента"
    );
  } finally {
    isLoading.value = false;
  }
};

const handleAudienceUpdate = (count: number) => {
  emits("updateAudienceCount", count);
};
</script>

<style lang="scss" scoped>
.counters-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;

  .refresh-button {
    color: $main-font-color;
    background: #fff;
    border: 2px solid rgba(107, 105, 232, 0.4);
    width: 100%;
    justify-content: center;
    box-shadow: 0px 1px 3px 0px rgba(107, 105, 232, 0.15);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover:not(:disabled) {
      border-color: $main-link-color;
      box-shadow: 0px 2px 6px 0px rgba(107, 105, 232, 0.2);
    }

    &:disabled {
      border-color: rgba(107, 105, 232, 0.4);
      opacity: 0.5;
      box-shadow: 0px 1px 3px 0px rgba(107, 105, 232, 0.1);
    }

    :deep(.p-button-label) {
      flex: none;
    }
  }
}
</style>
